import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  TrendingUp,
  Building2,
  DollarSign,
  BarChart3,
  FileText,
  ArrowUpRight
} from 'lucide-react'
import { motion } from 'framer-motion'
import { portfolioTrackerService } from '@/services/portfolioTrackerService'
import { 
  PortfolioTrackerAssetWithUnlock,
  PortfolioTrackerAssetSummary,
  AssetGraphData
} from '@/types/portfolio'
import { formatCurrency, formatDate, formatMoney } from '@/utils'
import { PageTemplate } from '@/components/templates'
import { Button, Input } from '@/components/atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CONTAINER_SIZES } from '@/constants'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

// Chart colors matching Figma design
const CHART_COLORS = {
  portfolioValue: '#a9c804',
  totalInvested: '#ff8e00',
  profitLoss: '#2d2923',
}

// Format large numbers compactly
const formatCompactNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) {
    return '—'
  }
  if (num >= 1e9) {
    return `${(num / 1e9).toFixed(1)}B`
  } else if (num >= 1e6) {
    return `${(num / 1e6).toFixed(1)}M`
  } else if (num >= 1e3) {
    return `${(num / 1e3).toFixed(1)}K`
  }
  return num.toFixed(0)
}

interface MetricCardProps {
  icon: 'invested' | 'current' | 'gain' | 'valuation'
  label: string
  value: string
  valuePrefix?: string
  percentChange?: {
    value: string
    isPositive: boolean
  }
}

const MetricCard = ({ icon, label, value, valuePrefix, percentChange }: MetricCardProps) => {
  const iconMap = {
    invested: DollarSign,
    current: ArrowUpRight,
    gain: TrendingUp,
    valuation: BarChart3,
  }
  const Icon = iconMap[icon]

  return (
    <div className="relative bg-white dark:bg-[#121212] h-[124px] overflow-hidden">
      <div className="absolute flex flex-col h-[76px] items-start justify-between left-[24px] top-[24px]">
        {/* Header with icon and label */}
        <div className="flex gap-[6px] items-center w-full">
          <div className="shrink-0 size-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            <Icon className="w-full h-full" strokeWidth={1.5} />
          </div>
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            {label}
          </p>
        </div>

        {/* Value */}
        <div className="flex gap-[4px] items-center w-full">
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[32px] text-[23px] tracking-[-0.2px]">
            {valuePrefix && (
              <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">{valuePrefix}</span>
            )}
            <span className="text-[#2d2923] dark:text-[#fdfdfd]">{value}</span>
          </p>
          {percentChange && (
            <div className="bg-[rgba(242,241,246,0.64)] dark:bg-[rgba(45,41,35,0.24)] flex gap-[4px] items-center justify-center pb-[4px] pl-[6px] pr-[8px] pt-[3px] rounded-[16px] shadow-[inset_1px_1px_4px_0px_rgba(217,214,227,0.4)]">
              <ArrowUpRight className={`size-[12px] ${percentChange.isPositive ? 'text-[#2ea66b]' : 'text-[#ff540c]'}`} strokeWidth={1.5} />
              <p className={`font-['Plus_Jakarta_Sans'] font-semibold leading-[16px] text-[12px] ${percentChange.isPositive ? 'text-[#2ea66b]' : 'text-[#ff540c]'}`}>
                {percentChange.value}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const AssetDetailScreen = () => {
  const { portfolioId, assetId } = useParams<{ portfolioId: string; assetId: string }>()
  const navigate = useNavigate()
  
  const [asset, setAsset] = useState<PortfolioTrackerAssetWithUnlock | null>(null)
  const [assetSummary, setAssetSummary] = useState<PortfolioTrackerAssetSummary | null>(null)
  const [graphData, setGraphData] = useState<AssetGraphData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Form states
  const [formData, setFormData] = useState({
    project_name: '',
    project_website: '',
    date: '',
    invested_amount: '',
    valuation: '',
    equity_or_tokens_amount: '',
    terms: ''
  })

  useEffect(() => {
    if (assetId) {
      loadData()
    }
  }, [assetId])

  const loadData = async () => {
    if (!assetId) return

    try {
      setLoading(true)
      setError(null)
      
      const [assetData, summaryData, graphDataResult] = await Promise.all([
        portfolioTrackerService.getAssetDetails(assetId),
        portfolioTrackerService.getAssetSummary(assetId),
        portfolioTrackerService.getAssetGraph(assetId)
      ])
      
      setAsset(assetData)
      setAssetSummary(summaryData)
      setGraphData(graphDataResult)
      
      // Set form data for editing
      setFormData({
        project_name: assetData.project_name || '',
        project_website: assetData.project_website || '',
        date: assetData.date || '',
        invested_amount: assetData.invested_amount?.toString() || '',
        valuation: assetData.valuation?.toString() || '',
        equity_or_tokens_amount: assetData.equity_or_tokens_amount?.toString() || '',
        terms: assetData.terms || ''
      })
    } catch (err) {
      setError('Failed to load asset data')
      console.error('Error loading asset data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleEditAsset = async () => {
    if (!assetId) return

    try {
      // Validate required fields
      if (!formData.project_name.trim()) {
        setError('Project name is required')
        return
      }

      if (!formData.date) {
        setError('Date is required')
        return
      }

      const investedAmount = parseFloat(formData.invested_amount)
      if (isNaN(investedAmount) || investedAmount <= 0) {
        setError('Invested amount must be a valid positive number')
        return
      }

      const valuation = parseFloat(formData.valuation)
      if (isNaN(valuation) || valuation <= 0) {
        setError('Valuation must be a valid positive number')
        return
      }

      // Parse equity amount
      let equityAmount: number | undefined
      if (formData.equity_or_tokens_amount && formData.equity_or_tokens_amount.trim()) {
        const parsed = parseFloat(formData.equity_or_tokens_amount)
        if (!isNaN(parsed)) {
          equityAmount = parsed
        }
      }

      const updatedAsset = await portfolioTrackerService.updateAsset(assetId, {
        project_name: formData.project_name.trim(),
        project_website: formData.project_website.trim() || undefined,
        date: formData.date,
        invested_amount: investedAmount,
        valuation: valuation,
        terms: formData.terms.trim(),
        equity_or_tokens_amount: equityAmount ?? 0
      })
      setAsset(prev => prev ? { ...prev, ...updatedAsset } : null)
      setShowEditModal(false)
    } catch (err) {
      setError('Failed to update asset')
      console.error('Error updating asset:', err)
    }
  }

  const handleDeleteAsset = async () => {
    if (!assetId || !asset) return

    try {
      setDeleting(true)
      await portfolioTrackerService.deleteAsset(assetId)
      navigate(`/portfolio/${portfolioId}`)
    } catch (err) {
      setError('Failed to delete asset')
      console.error('Error deleting asset:', err)
      setDeleting(false)
    }
  }

  if (loading) {
    return <PageTemplate isLoading />
  }

  if (error || !asset) {
    return (
      <PageTemplate
        title="Asset Not Found"
        subtitle="The requested asset could not be loaded"
        maxWidth={CONTAINER_SIZES.WIDE}
      >
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="mx-auto text-muted-foreground mb-4" size={48} />
            <h3 className="text-lg font-semibold mb-2">Asset Not Found</h3>
            <p className="text-muted-foreground mb-4">
              {error || 'The asset you are looking for does not exist or you do not have access to it.'}
            </p>
            <Button
              variant="primary"
              leftIcon={<ArrowLeft size={16} />}
              onClick={() => navigate(`/portfolio/${portfolioId}`)}
            >
              Back to Portfolio
            </Button>
          </CardContent>
        </Card>
      </PageTemplate>
    )
  }

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
          >
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </motion.div>
        )}

        {/* Header Actions Row */}
        <div className="flex items-center justify-between w-full">
          <button
            onClick={() => navigate(`/portfolio/${portfolioId}`)}
            className="bg-white dark:bg-[#121212] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] flex gap-[4px] items-center justify-center px-[16px] py-[8px] hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
          >
            <ArrowLeft className="shrink-0 size-[16px] text-[#2d2923] dark:text-[#fdfdfd]" strokeWidth={1.5} />
            <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[16px] text-[#2d2923] dark:text-[#fdfdfd] text-[12px]">
              Back to Portfolio
            </p>
          </button>
          <div className="flex gap-[18px] items-center">
            <button
              onClick={() => setShowEditModal(true)}
              className="bg-white dark:bg-[#121212] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] flex items-center justify-center px-[24px] py-[8px] hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
            >
              <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[16px] text-[#2d2923] dark:text-[#fdfdfd] text-[12px]">
                Edit Asset
              </p>
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="bg-white dark:bg-[#121212] border border-[#e5907f] flex items-center justify-center px-[24px] py-[8px] hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
            >
              <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[16px] text-[#ff540c] text-[12px]">
                Delete Asset
              </p>
            </button>
          </div>
        </div>

        {/* Page Header */}
        <div className="flex flex-col items-start">
          <div className="flex gap-[10px] items-center mb-1">
            <FileText className="shrink-0 size-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" strokeWidth={1.5} />
            <h1 className="font-['Plus_Jakarta_Sans'] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-[16px]">
              {asset.project_name}
            </h1>
          </div>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[24px] text-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            Asset details and performance tracking
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column - Metric Cards */}
          <div className="space-y-4">
            {/* Top Row - Invested Amount & Current Value */}
            <div className="grid grid-cols-2 gap-4">
              <MetricCard
                icon="invested"
                label="Invested Amount"
                value={assetSummary ? formatCompactNumber(assetSummary.invested_amount) : '—'}
                valuePrefix="$ "
              />
              <MetricCard
                icon="current"
                label="Current Value"
                value={assetSummary ? formatCompactNumber(assetSummary.current_value) : '—'}
                valuePrefix="$ "
              />
            </div>
            {/* Bottom Row - Gain/Loss & Project Valuation */}
            <div className="grid grid-cols-2 gap-4">
              <MetricCard
                icon="gain"
                label="Gain/Loss"
                value={assetSummary ? formatCompactNumber(assetSummary.gain_loss_usd) : '—'}
                valuePrefix="$ "
                percentChange={assetSummary ? {
                  value: `${Math.abs(assetSummary.gain_loss_percentage).toFixed(0)}%`,
                  isPositive: assetSummary.gain_loss_percentage >= 0
                } : undefined}
              />
              <MetricCard
                icon="valuation"
                label="Project Valuation"
                value={assetSummary ? formatCompactNumber(assetSummary.project_valuation) : '—'}
                valuePrefix="$ "
              />
            </div>
          </div>

          {/* Right Column - Asset Information */}
          <div className="bg-white dark:bg-[#121212] relative p-[32px] h-[264px]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <p className="font-['Plus_Jakarta_Sans'] font-semibold leading-[18px] text-[13px] text-[rgba(45,41,35,0.84)] dark:text-[rgba(253,253,253,0.84)] uppercase">
                Asset Information
              </p>
              {assetSummary && (
                <div className="flex gap-[6px] items-center">
                  <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    Valuation Source:
                  </p>
                  <div className="bg-[rgba(45,41,35,0.06)] dark:bg-[rgba(253,253,253,0.1)] flex items-center justify-center px-[4px] py-[2px]">
                    <p className="font-semibold leading-[16px] text-[#2d2923] dark:text-[#fdfdfd] text-[12px]">
                      {assetSummary.valuation_source}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Project Badge */}
            <div className="bg-white dark:bg-[#1a1a1a] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] inline-flex gap-[10px] items-center px-[12px] py-[6px] mb-4">
              <FileText className="shrink-0 size-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" strokeWidth={1.5} />
              <p className="font-['Plus_Jakarta_Sans'] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-[16px]">
                {asset.project?.name || asset.project_name}
              </p>
            </div>

            {asset.project_website && (
              <a 
                href={asset.project_website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] underline ml-4"
              >
                Visit Website
              </a>
            )}

            {/* Info Grid Row 1 */}
            <div className="grid grid-cols-3 gap-[18px] mt-6">
              <div className="flex flex-col gap-[4px]">
                <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Investment Date
                </p>
                <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                  {formatDate(asset.date)}
                </p>
              </div>
              <div className="flex flex-col gap-[4px]">
                <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Invested Amount
                </p>
                <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                  {formatMoney(asset.invested_amount)}
                </p>
              </div>
              <div className="flex flex-col gap-[4px]">
                <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Project Valuation
                </p>
                <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                  {formatMoney(asset.valuation)}
                </p>
              </div>
            </div>

            {/* Info Grid Row 2 */}
            <div className="grid grid-cols-3 gap-[18px] mt-4">
              <div className="flex flex-col gap-[4px]">
                <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Equity/Token Amount
                </p>
                <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                  {asset.equity_or_tokens_amount || '—'}
                </p>
              </div>
              <div className="flex flex-col gap-[4px]">
                <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Terms
                </p>
                <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                  {asset.terms || '—'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row - Unlock Schedule & Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Unlock Schedule */}
          <div className="bg-white dark:bg-[#121212] p-[32px] h-[465px]">
            <p className="font-['Plus_Jakarta_Sans'] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-[20px] tracking-[-0.2px] mb-6">
              Unlock Schedule
            </p>

            {asset.unlock ? (
              <>
                {/* Info Row 1 */}
                <div className="grid grid-cols-2 gap-[18px] mb-4">
                  <div className="flex flex-col gap-[4px]">
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      Total Amount
                    </p>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                      {formatCurrency(asset.unlock.total_amount)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-[4px]">
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      Unlock Type
                    </p>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                      {asset.unlock.unlock_type}
                    </p>
                  </div>
                </div>

                {/* Info Row 2 */}
                <div className="grid grid-cols-2 gap-[18px] mb-6">
                  <div className="flex flex-col gap-[4px]">
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      Start Date
                    </p>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                      {formatDate(asset.unlock.start_date)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-[4px]">
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      End Date
                    </p>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                      {formatDate(asset.unlock.end_date)}
                    </p>
                  </div>
                </div>

                {/* Unlocked/Locked Box */}
                <div className="bg-white dark:bg-[#1a1a1a] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] flex justify-between items-start px-[20px] py-[16px] mb-6">
                  <div className="flex flex-col gap-[4px]">
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[20px] text-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      Unlocked
                    </p>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-[16px]">
                      {formatCurrency(asset.unlock.summary.total_amount_unlocked)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-[4px] items-end">
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[20px] text-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      Locked
                    </p>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-[16px]">
                      {asset.unlock.summary.total_amount_locked > 0 ? formatCurrency(asset.unlock.summary.total_amount_locked) : '—'}
                    </p>
                  </div>
                </div>

                {/* Progress */}
                <div className="flex justify-between items-center mb-2">
                  <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    Progress
                  </p>
                  <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[#2d2923] dark:text-[#fdfdfd] text-[13px]">
                    {asset.unlock.summary.unlocked_percent.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(253,253,253,0.12)] h-[11px] rounded-[8px] overflow-hidden mb-4">
                  <div 
                    className="bg-[#a9c804] h-full rounded-[8px] transition-all duration-300"
                    style={{ width: `${asset.unlock.summary.unlocked_percent}%` }}
                  />
                </div>

                {/* Upcoming Unlocks */}
                <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Upcoming Unlocks
                </p>
                {asset.unlock.allocations.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {asset.unlock.allocations
                      .filter(allocation => new Date(allocation.unlock_date) > new Date())
                      .slice(0, 3)
                      .map((allocation, index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-[#1a1a1a] rounded">
                          <div>
                            <p className="text-sm font-medium">{formatDate(allocation.unlock_date)}</p>
                            <p className="text-xs text-muted-foreground">{allocation.percent_of_total}%</p>
                          </div>
                          <p className="text-sm font-medium">{formatCurrency(allocation.amount)}</p>
                        </div>
                      ))}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No unlock schedule available
              </div>
            )}
          </div>

          {/* Asset Performance Over Time Chart */}
          <div className="bg-white dark:bg-[#121212] h-[465px] relative p-[32px]">
            <p className="font-['Plus_Jakarta_Sans'] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-[20px] tracking-[-0.2px] mb-6">
              Asset Performance Over Time
            </p>

            {graphData && graphData.monthly_values.length > 0 ? (
              <>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={graphData.monthly_values}
                      margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="1 1" stroke="#e0e0e0" vertical={false} />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(val) => {
                          const date = new Date(val)
                          return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(2)}`
                        }}
                        tick={{ fontSize: 12, fill: '#878787' }}
                        tickMargin={10}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        tickFormatter={(value) => `$${formatCompactNumber(value)}`}
                        tick={{ fontSize: 16, fill: '#878787' }}
                        width={60}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip 
                        formatter={(value: number, name: string) => {
                          const labels: {[key: string]: string} = {
                            total_value: 'Portfolio Value',
                            total_invested: 'Total Invested',
                            gain_loss_usd: 'Profit/Loss'
                          }
                          return [formatMoney(value), labels[name] || name]
                        }}
                        labelFormatter={(label) => formatDate(label)}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="total_value" 
                        name="Portfolio Value"
                        stroke={CHART_COLORS.portfolioValue}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="total_invested" 
                        name="Total Invested"
                        stroke={CHART_COLORS.totalInvested}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="gain_loss_usd" 
                        name="Profit/Loss"
                        stroke={CHART_COLORS.profitLoss}
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {/* Custom Legend */}
                <div className="flex gap-[32px] items-center justify-center mt-4">
                  <div className="flex gap-[12px] items-center">
                    <div className="bg-[#a9c804] h-[8px] w-[19px]" />
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                      Portfolio Value
                    </p>
                  </div>
                  <div className="flex gap-[12px] items-center">
                    <div className="bg-[#ff8e00] h-[8px] w-[19px]" />
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                      Total Invested
                    </p>
                  </div>
                  <div className="flex gap-[12px] items-center">
                    <div className="bg-[#2d2923] dark:bg-[#fdfdfd] h-[8px] w-[19px]" />
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                      Profit/Loss
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No performance data available
              </div>
            )}
          </div>
        </div>

        {/* Edit Asset Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>Edit Asset</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Project Name *</label>
                  <Input
                    value={formData.project_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, project_name: e.target.value }))}
                    placeholder="Enter project name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Project Website</label>
                  <Input
                    value={formData.project_website}
                    onChange={(e) => setFormData(prev => ({ ...prev, project_website: e.target.value }))}
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Investment Date *</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Invested Amount (USD) *</label>
                  <Input
                    type="number"
                    value={formData.invested_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, invested_amount: e.target.value }))}
                    placeholder="50000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Project Valuation (USD) *</label>
                  <Input
                    type="number"
                    value={formData.valuation}
                    onChange={(e) => setFormData(prev => ({ ...prev, valuation: e.target.value }))}
                    placeholder="1000000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Equity/Token Amount *</label>
                  <Input
                    type="number"
                    value={formData.equity_or_tokens_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, equity_or_tokens_amount: e.target.value }))}
                    placeholder="100000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Terms *</label>
                  <Input
                    value={formData.terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                    placeholder="SAFE agreement, equity, tokens, etc."
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleEditAsset}
                    disabled={!formData.project_name || !formData.date || !formData.invested_amount || !formData.valuation || !formData.equity_or_tokens_amount || !formData.terms}
                    className="flex-1"
                  >
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Asset Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Delete Asset</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Are you sure you want to delete "{asset.project_name}"? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDeleteAsset}
                    disabled={deleting}
                    className="flex-1 text-red-500 border-red-500 hover:bg-red-500/10"
                  >
                    {deleting ? 'Deleting...' : 'Delete Asset'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </PageTemplate>
  )
}

export default AssetDetailScreen
